// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { Button, Modal, TextArea, InlineNotification } from '@carbon/react';
import { ArrowLeft } from '@carbon/icons-react';
import NotFound from '../../NotFound';
import { usePlanDetail } from '../../../hooks/usePlanDetail';
import { useNow } from '../../../hooks/useNow';
import { PageHead, Loading, EmptyState } from '../../../components/shell/ScreenHelpers';
import { getNumberAttr, executionDayPassed } from '../../../utils/planAttributes';
import { canReviewPlans } from '../../../utils/planReview';
import { withStandardNarration } from '../../../utils/planNarration';
import { useAuth } from '../../../hooks/useAuth';
import { useSiteTime } from '../../../hooks/useSiteTime';
import { getEnergySavings } from '../../../utils/planSeries';
import { PlanDetailContext } from './PlanDetailContext';
import PlanDriversTab from './PlanDriversTab';
import PlanActionsTab from './PlanActionsTab';
import PlanAuditTrail from './PlanAuditTrail';
import { formatCalendarDate } from '../../../utils/siteTime';
import './PlanDetail.css';

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const TAB_KEYS = ['drivers', 'actions', 'audit'] as const;
const TAB_LABELS: Record<(typeof TAB_KEYS)[number], string> = {
  drivers: 'Drivers & Impacts',
  actions: 'Actions',
  audit: 'Audit',
};

const EXPIRED_MESSAGE = 'The execution day has passed, so this plan can no longer be reviewed.';

/** How often the page rechecks expiry, so a site midnight reached while it is open is noticed. */
const EXPIRY_CHECK_MS = 30_000;

function PlanDetail() {
  const { approvalId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    plan,
    planFile: loadedPlanFile,
    loading,
    error,
    updateStatus,
  } = usePlanDetail(approvalId);
  // Older payloads are mapped onto the same drivers/actions layout as v1 plans.
  const planFile = useMemo(
    () => (loadedPlanFile ? withStandardNarration(loadedPlanFile) : loadedPlanFile),
    [loadedPlanFile]
  );
  const { timeZone } = useSiteTime();
  const { effectiveRole } = useAuth();
  const canReview = canReviewPlans(effectiveRole);
  const now = useNow(EXPIRY_CHECK_MS);
  const [pendingDecision, setPendingDecision] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const basePath = `/optimizations/${approvalId}`;

  // A plan nobody decided before its execution day can no longer be reviewed:
  // the lists file it under Past, so the detail must not offer a decision the
  // plan can no longer act on. A plan with no execution day is never expired
  // here (there is nothing to compare), so it stays reviewable even though the
  // lists file it under Past.
  const expiredAt = (at: Date) =>
    plan?.status === 'PENDING' && executionDayPassed(plan.attributes, at, timeZone);
  const expired = expiredAt(now);

  // A confirmation left open across the site's midnight can no longer be sent.
  useEffect(() => {
    if (!expired) return;
    setPendingDecision(null);
    setComment('');
    setSubmitError(null);
  }, [expired]);
  const tabKeys: readonly string[] = TAB_KEYS;
  const tabLabels: Record<string, string> = TAB_LABELS;

  const getActiveIndex = (): number => {
    const path = location.pathname;
    for (let i = tabKeys.length - 1; i >= 1; i--) {
      if (path.endsWith(`/${tabKeys[i]}`)) return i;
    }
    return 0;
  };

  const handleTabChange = ({ selectedIndex }: { selectedIndex: number }) => {
    const key = tabKeys[selectedIndex];
    navigate(key === tabKeys[0] ? basePath : `${basePath}/${key}`, { state: location.state });
  };

  const goBack = () => {
    const from = (location.state as { from?: string } | null)?.from;
    if (from) return navigate(from);
    return location.key !== 'default' ? navigate(-1) : navigate('/recommendations/live');
  };

  const backLink = (
    <Button kind="ghost" size="sm" renderIcon={ArrowLeft} onClick={goBack}>
      Back
    </Button>
  );

  const closeReview = () => {
    setPendingDecision(null);
    setComment('');
    setSubmitError(null);
  };

  const submitReview = async () => {
    if (!pendingDecision || !canReview) return;
    // The clock tick can lag behind midnight, so check the current time too.
    if (expiredAt(new Date())) {
      setSubmitError(EXPIRED_MESSAGE);
      return;
    }
    try {
      setSubmitting(true);
      setSubmitError(null);
      await updateStatus(pendingDecision, comment.trim() || undefined);
      closeReview();
    } catch (err) {
      setSubmitError((err as Error).message || 'Failed to submit the decision. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const statusLabel = expired
    ? 'Expired — not reviewed'
    : plan
      ? (STATUS_LABELS[plan.status] ?? plan.status)
      : '';

  const headerReviewActions = expired ? (
    <span className="pd-expired-note">{EXPIRED_MESSAGE}</span>
  ) : canReview && plan?.status === 'PENDING' ? (
    <>
      <Button
        kind="secondary"
        size="md"
        disabled={submitting}
        onClick={() => setPendingDecision('REJECTED')}
      >
        Reject
      </Button>
      <Button
        kind="primary"
        size="md"
        disabled={submitting}
        onClick={() => setPendingDecision('APPROVED')}
      >
        Approve
      </Button>
    </>
  ) : plan?.status === 'PENDING' && effectiveRole ? (
    // A read-only viewer would otherwise see a pending plan with no controls and no reason.
    <span className="pd-expired-note">Awaiting an Ops Manager&apos;s decision.</span>
  ) : null;

  const isReject = pendingDecision === 'REJECTED';
  const reviewModal = (
    <Modal
      open
      danger={isReject}
      modalHeading={isReject ? 'Reject this plan' : 'Approve this plan'}
      primaryButtonText={submitting ? 'Submitting…' : 'Submit'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={submitting}
      onRequestClose={closeReview}
      onRequestSubmit={submitReview}
      size="sm"
    >
      <p className="sched-modal-note">
        {isReject
          ? 'The plan will not execute. Your comment is recorded on the audit trail.'
          : 'The plan is cleared to execute in its stated window. Your comment is recorded on the audit trail.'}
      </p>
      <TextArea
        id="plan-review-comment"
        labelText="Comment (optional)"
        placeholder={
          isReject ? 'Why is this being rejected?' : 'Anything the operator should know…'
        }
        value={comment}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
        disabled={submitting}
        rows={4}
      />
      {submitError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Couldn't submit"
          subtitle={submitError}
          hideCloseButton
          className="sched-modal-error"
        />
      )}
    </Modal>
  );

  if (loading) {
    return (
      <div className="hs-page">
        <PageHead title="Loading plan…" actions={backLink} />
        <Loading label="Loading plan details…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="hs-page">
        <PageHead title="Failed to load plan" actions={backLink} />
        <EmptyState
          big="Couldn't load this plan."
          sub={typeof error === 'string' ? error : 'An unexpected error occurred.'}
        />
      </div>
    );
  }

  if (!plan || !planFile) {
    return <NotFound />;
  }

  const totalCostSavings = getNumberAttr(plan.attributes, 'totalCostSavings');
  const energySavings = getEnergySavings(planFile.raw_inputs);
  const energyVsBaseline =
    getNumberAttr(plan.attributes, 'energyVsBaselinePct') ?? energySavings?.percentVsBaseline;
  const energySavedKwh =
    getNumberAttr(plan.attributes, 'energyVsBaselineKwh') ?? energySavings?.savedKwh;
  const hasSavings = energyVsBaseline !== undefined || totalCostSavings !== undefined;
  const daySummaryStrip = hasSavings ? (
    <div className="pd-summary-strip">
      {energyVsBaseline !== undefined && (
        <div className="pd-summary-cell">
          <div className="pd-summary-label">Energy saved vs. baseline</div>
          <div className="pd-summary-value">
            {`${Math.abs(energyVsBaseline).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`}
            {energySavedKwh !== undefined &&
              ` (${Math.abs(energySavedKwh).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })} kWh)`}
          </div>
          <div className="pd-summary-sub">shifted out of the peak window</div>
        </div>
      )}
      {totalCostSavings !== undefined && (
        <div className="pd-summary-cell">
          <div className="pd-summary-label">Expected utility bill savings</div>
          <div className="pd-summary-value">
            {`$${Math.abs(totalCostSavings).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          </div>
          <div className="pd-summary-sub">Est · reconciled monthly.</div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <PlanDetailContext.Provider value={{ plan, planFile, expired }}>
      <div className="hs-page pd-page">
        <div className="pd-detail-head">
          <button type="button" className="pd-back-link" onClick={goBack}>
            <ArrowLeft size={16} />
            Back to recommendations
          </button>
          <div className="pd-head-row">
            <div className="pd-head-titles">
              <div className="pd-head-date">{formatCalendarDate(plan.date)}</div>
              <h1 className="pd-head-title">{planFile.title}</h1>
            </div>
            <div className="pd-head-actions">
              <span
                className={`pd-status pd-status-${expired ? 'expired' : plan.status.toLowerCase()}`}
              >
                {statusLabel}
              </span>
              {headerReviewActions}
            </div>
          </div>
        </div>
        {daySummaryStrip}

        <div className="pd-tabs" role="tablist" aria-label="Plan sections">
          {tabKeys.map((key, i) => {
            const active = getActiveIndex() === i;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`pd-tab${active ? ' pd-tab-active' : ''}`}
                onClick={() => handleTabChange({ selectedIndex: i })}
              >
                {tabLabels[key]}
              </button>
            );
          })}
        </div>

        <div className="sched-tab-content">
          <Routes>
            <Route path="/" element={<PlanDriversTab />} />
            <Route path="actions" element={<PlanActionsTab />} />
            <Route path="audit" element={<PlanAuditTrail />} />
            {/* The old overview/assets/kpis/execution tabs are gone: an old link lands on Drivers. */}
            <Route path="*" element={<Navigate to={basePath} replace state={location.state} />} />
          </Routes>
          {pendingDecision !== null && !expired && reviewModal}
        </div>
      </div>
    </PlanDetailContext.Provider>
  );
}

export default PlanDetail;
